import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {RouterLink} from '@angular/router';
import {MatButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatFormField, MatPrefix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {CodaltComponent} from '../codalt.component';
import {Platform} from '../classes/platform';
import {PlatformService} from '../services/platform.service';
import {level, level as LevelEnum} from '../classes/checkpoint';
import {PlatformLevelStatusPipe} from '../pipes/platform-level-status.pipe';
import {HasFeaturePipe} from '../pipes/has-feature.pipe';
import {DaysFromNowPipe} from '../pipes/days-from-now.pipe';
import {MinDatePipe} from '../generic/pipes/min-date.pipe';
import {Server} from '../classes/server';
import {ServerService} from '../services/server.service';
import {AuthorisationService} from '../services/auth/authorisation.service';
import {Features} from '../services/auth/features';
import {CheckpointStatusOrbsEnum} from '../classes/checkpoint-status-orbs.enum';
import {
    CheckStripComponent,
    CheckStripItem,
    DataListComponent,
    DataRowComponent,
    DayGroupComponent,
    EmptyStateComponent,
    EnvBadgeComponent,
    FilterOption,
    FilterTilesComponent,
    PageComponent,
    PageHeaderComponent,
    RelativeDayPipe,
    SectionComponent,
    StatusBadgeComponent,
    StatusDotComponent,
    Tone
} from '../shared/ui';
import {calendarDaysFromToday, EnvUrl, orbDisplay, platformEnvUrls, ServerDisplay, serverDisplay, SSL_WARN_DAYS, sslTone} from './seire-display';

const LEVEL_PRIORITY: Record<string, number> = {
    [level.PRODUCTION]: 1,
    [level.HEALTH]: 2,
    [level.CHECK]: 3,
    [level.ACCEPTANCE]: 4,
};

const LEVEL_DATA_MAP = {
    [level.PRODUCTION]: (p: Platform) => ({
        count: p.checkpoint_production_count,
        errorCount: p.checkpoint_production_error_count,
        warningCount: p.checkpoint_production_warning_count,
        alertCount: p.checkpoint_production_postpone_count,
        lastInstance: p.checkpoint_production_last_instance?.[0]?.updated_at ?? null,
    }),
    [level.HEALTH]: (p: Platform) => ({
        count: p.checkpoint_health_count,
        errorCount: p.checkpoint_health_error_count,
        warningCount: p.checkpoint_health_warning_count,
        alertCount: p.checkpoint_health_postpone_count,
        lastInstance: p.checkpoint_health_last_instance?.[0]?.updated_at ?? null,
    }),
    [level.CHECK]: (p: Platform) => ({
        count: p.checkpoint_check_count,
        errorCount: p.checkpoint_check_error_count,
        warningCount: p.checkpoint_check_warning_count,
        alertCount: p.checkpoint_check_postpone_count,
        lastInstance: p.checkpoint_check_last_instance?.[0]?.updated_at ?? null,
    }),
    [level.ACCEPTANCE]: (p: Platform) => ({
        count: p.checkpoint_acceptance_count,
        errorCount: p.checkpoint_acceptance_error_count,
        warningCount: p.checkpoint_acceptance_warning_count,
        alertCount: p.checkpoint_acceptance_postpone_count,
        lastInstance: p.checkpoint_acceptance_last_instance?.[0]?.updated_at ?? null,
    }),
};

type PlatformFilter = 'all' | 'errorProduction' | 'errorOther' | 'ssl' | 'none';

interface PlatformStatus {
    key: 'error' | 'warning' | 'alert' | 'ok' | 'none';
    rank: number;
    tone: Tone;
    label: string;
}

interface PlatformRow {
    platform: Platform;
    status: PlatformStatus;
    /** The Production level has a failure (Storing productie); other failures are Storing overig. */
    productionError: boolean;
    strip: CheckStripItem[];
    sslDate: Date | null;
    sslTone: 'error' | 'warn' | null;
    sslWarn: boolean;
    envs: EnvUrl[];
    search: string;
}

interface PlatformGroup {
    company: string | null;
    rows: PlatformRow[];
}

const FILTER_LABELS: Record<PlatformFilter, string> = {
    all: 'Alle platformen',
    errorProduction: 'Storing productie',
    errorOther: 'Storing overig',
    ssl: `SSL verloopt < ${SSL_WARN_DAYS} dagen`,
    none: 'Geen checks',
};

@Component({
    selector: 'app-seire',
    templateUrl: './seire.component.html',
    styleUrls: ['./seire.component.scss'],
    imports: [
        RouterLink,
        HasFeaturePipe,
        DaysFromNowPipe,
        DatePipe,
        MatButton,
        MatCheckbox,
        MatFormField,
        MatInput,
        MatPrefix,
        PageComponent,
        PageHeaderComponent,
        FilterTilesComponent,
        SectionComponent,
        DataListComponent,
        DataRowComponent,
        DayGroupComponent,
        StatusDotComponent,
        StatusBadgeComponent,
        CheckStripComponent,
        EnvBadgeComponent,
        EmptyStateComponent,
        RelativeDayPipe,
    ]
})
export class SeireComponent extends CodaltComponent implements OnInit, OnDestroy {
    readonly platforms = signal<Platform[]>(null);
    readonly servers = signal<Server[]>([]);
    levelEnums = Object.values(LevelEnum);

    readonly filter = signal<PlatformFilter>('all');
    readonly query = signal('');
    readonly groupByCompany = signal(false);

    private readonly levelStatusPipe = new PlatformLevelStatusPipe();
    private readonly minDatePipe = new MinDatePipe();

    readonly sortedPlatforms = computed(() => {
        const list = this.platforms();
        if (!list?.length) {
            return list;
        }

        return [...list].sort((a, b) => {
            const statusA = this.getWorstPlatformStatusPriority(a);
            const statusB = this.getWorstPlatformStatusPriority(b);
            if (statusA !== statusB) {
                return statusA - statusB;
            }
            return a.name.localeCompare(b.name); // tie-break alphabetically
        });
    });

    /** Display rows; failures (as shown by the status dot) on top, otherwise the order of sortedPlatforms. */
    readonly rows = computed<PlatformRow[]>(() =>
        (this.sortedPlatforms() ?? [])
            .map(platform => this.toRow(platform))
            .sort((a, b) => a.status.rank - b.status.rank)
    );

    readonly filterOptions = computed<FilterOption<PlatformFilter>[]>(() => {
        const loaded = this.platforms() !== null;
        const rows = this.rows();
        const count = (filter: PlatformFilter) => loaded ? rows.filter(row => this.matchesFilter(row, filter)).length : null;
        // Dot colors: neutral, error (lighter for Storing overig), warn and inactive dots from the design tokens
        return [
            {value: 'all', label: FILTER_LABELS.all, color: '#6b7280', count: count('all')},
            {value: 'errorProduction', label: FILTER_LABELS.errorProduction, color: '#d93025', count: count('errorProduction')},
            {value: 'errorOther', label: FILTER_LABELS.errorOther, color: '#f28b82', count: count('errorOther')},
            {value: 'ssl', label: FILTER_LABELS.ssl, color: '#e0a100', count: count('ssl')},
            {value: 'none', label: FILTER_LABELS.none, color: '#c9ccd1', count: count('none')},
        ];
    });

    readonly listTitle = computed(() => FILTER_LABELS[this.filter()] ?? FILTER_LABELS.all);

    readonly groups = computed<PlatformGroup[]>(() => {
        const filter = this.filter();
        const query = this.query().trim().toLowerCase();
        const rows = this.rows().filter(row => this.matchesFilter(row, filter) && (!query || row.search.includes(query)));
        if (!this.groupByCompany()) {
            return [{company: null, rows}];
        }
        const companyName = (row: PlatformRow) => row.platform.company?.name ?? '';
        const groups: PlatformGroup[] = [];
        [...rows]
            .sort((a, b) => companyName(a).localeCompare(companyName(b)) || a.status.rank - b.status.rank)
            .forEach(row => {
                const company = companyName(row);
                if (groups[groups.length - 1]?.company !== company) {
                    groups.push({company, rows: []});
                }
                groups[groups.length - 1].rows.push(row);
            });
        return groups;
    });

    readonly hasVisibleRows = computed(() => this.groups().some(group => group.rows.length > 0));

    readonly serverRows = computed<ServerDisplay[]>(() => (this.servers() ?? []).map(server => serverDisplay(server)));

    readonly serversMeta = computed(() => {
        const rows = this.serverRows();
        if (!rows.length) {
            return null;
        }
        const stale = rows.filter(row => row.stale).length;
        return stale ? `${stale} server${stale === 1 ? '' : 's'} langer dan een dag niet bijgewerkt` : 'Alle servers recent bijgewerkt';
    });

    constructor(
        private platformService: PlatformService,
        private serverService: ServerService
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(
            this.platformService.getPlatformsLive().subscribe({
                next: (platforms) => {
                    this.platforms.set(platforms.map(p => ({...p})));
                },
                error: err => console.error(err)
            })
        );

        if (AuthorisationService.hasFeature(Features.Servers)) {
            this.subscriptions.add(
                this.serverService.getServers().subscribe(servers => {
                    this.servers.set(servers);
                })
            );
        }
    }

    getPlatformLevelStatusPriority(levelKey: string, platform: Platform): number {
        const data = LEVEL_DATA_MAP[levelKey]?.(platform);
        if (!data?.count) {
            return 5; // inactive / no checkpoints
        }

        const timeBehind = new Date();
        timeBehind.setDate(timeBehind.getDate() - 1);
        const lastInstance = data.lastInstance ? new Date(data.lastInstance) : null;

        if (data.errorCount !== 0 || (lastInstance !== null && lastInstance < timeBehind)) {
            return 1; // error
        }
        if (data.warningCount !== 0) {
            return 2; // warning
        }
        if (data.alertCount) {
            return 3; // alert
        }
        return 4; // ok
    }

    getWorstPlatformStatusPriority(platform: Platform): number {
        const scores = Object.keys(LEVEL_DATA_MAP).map(l => ({
            levelPriority: LEVEL_PRIORITY[l],
            statusPriority: this.getPlatformLevelStatusPriority(l, platform),
        }));
        const activeScores = scores.filter(s => s.statusPriority < 5); // 5 = inactive
        if (!activeScores.length) {
            return 999; // no active levels → sort to bottom
        }
        const worstStatus = Math.min(...activeScores.map(s => s.statusPriority));
        const bestLevelWithWorstStatus = Math.min(
            ...activeScores
                .filter(s => s.statusPriority === worstStatus)
                .map(s => s.levelPriority)
        );
        return worstStatus * 10 + bestLevelWithWorstStatus;
    }

    private toRow(platform: Platform): PlatformRow {
        // One bar per checkpoint level, colored by the existing PlatformLevelStatusPipe
        const levelOrbs = this.levelEnums.map(levelKey => ({levelKey, orb: this.levelStatusPipe.transform(levelKey, platform)}));
        const strip = levelOrbs.map(({levelKey, orb}) => ({
            tone: orbDisplay(orb).strip,
            title: `${levelKey}: ${orbDisplay(orb).label}`,
            // As before, a level opens the platform with only the checkpoints of that level
            link: ['/', this.Routenames.seire, platform.id],
            queryParams: {level: levelKey}
        }));
        const sslDate = this.minDatePipe.transform(platform.closest_ssl_expiry_date, platform.closest_acceptance_ssl_expiry_date);
        const sslDays = calendarDaysFromToday(sslDate);
        const envs = platformEnvUrls(platform);
        return {
            platform,
            status: this.platformStatus(levelOrbs.map(l => l.orb)),
            productionError: levelOrbs.some(l => l.levelKey === level.PRODUCTION && l.orb === CheckpointStatusOrbsEnum.error),
            strip,
            sslDate,
            sslTone: sslTone(sslDate),
            sslWarn: sslDays !== null && sslDays < SSL_WARN_DAYS,
            envs,
            search: [platform.name, platform.company?.name, ...envs.map(env => env.url)].join(' ').toLowerCase(),
        };
    }

    /** Worst level wins; without any checkpoints the platform has no checks. */
    private platformStatus(orbs: string[]): PlatformStatus {
        const has = (orb: CheckpointStatusOrbsEnum) => orbs.includes(orb);
        if (has(CheckpointStatusOrbsEnum.error)) {
            return {key: 'error', rank: 0, tone: 'error', label: 'Storing'};
        }
        if (has(CheckpointStatusOrbsEnum.warning)) {
            return {key: 'warning', rank: 1, tone: 'warn', label: 'Waarschuwing'};
        }
        if (has(CheckpointStatusOrbsEnum.alert)) {
            return {key: 'alert', rank: 2, tone: 'warn', label: 'Opgeschort'};
        }
        if (has(CheckpointStatusOrbsEnum.success)) {
            return {key: 'ok', rank: 3, tone: 'ok', label: 'Alles goed'};
        }
        return {key: 'none', rank: 4, tone: 'neutral', label: 'Geen checks'};
    }

    private matchesFilter(row: PlatformRow, filter: PlatformFilter): boolean {
        switch (filter) {
            case 'errorProduction':
                return row.status.key === 'error' && row.productionError;
            case 'errorOther':
                return row.status.key === 'error' && !row.productionError;
            case 'ssl':
                return row.sslWarn;
            case 'none':
                return row.status.key === 'none';
            default:
                return true;
        }
    }
}
